/**
 * SignUpPage Component
 * 
 * Two-step registration form for creating new store accounts.
 * Step 1: Store information (name, phone, location)
 * Step 2: Owner information (name, email, password)
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onBack - Callback to go back to previous screen
 * @param {Function} props.onSuccess - Callback when signup succeeds (receives user, token)
 * @param {string|null} props.selectedPlan - Selected subscription plan ID (optional)
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../ui/card';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function SignUpPage({ onBack, onSuccess, selectedPlan }) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = Store Info, 2 = Owner Info
  const [error, setError] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [storeData, setStoreData] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    address: ''
  });
  
  const [ownerData, setOwnerData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleStoreNext = () => {
    if (!storeData.name || !storeData.phone) {
      setError('Store name and phone are required');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSignUp = async () => {
    console.log('=== SIGNUP STARTED ===');
    console.log('Store data:', storeData);
    console.log('Owner data:', { ...ownerData, password: '***', confirmPassword: '***' });
    
    if (!ownerData.name || !ownerData.email || !ownerData.password) {
      setError('All fields are required');
      console.error('Validation failed: Missing required fields');
      return;
    }
    if (ownerData.password !== ownerData.confirmPassword) {
      setError('Passwords do not match');
      console.error('Validation failed: Passwords do not match');
      return;
    }
    if (ownerData.password.length < 8) {
      setError('Password must be at least 8 characters');
      console.error('Validation failed: Password too short');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Create store and owner in single call
      console.log('Creating store and owner via signup endpoint...');
      
      const signupResponse = await axios.post(`${API}/stores/signup`, {
        // Store data
        name: storeData.name,
        phone: storeData.phone,
        city: storeData.city || '',
        state: storeData.state || '',
        address: storeData.address || '',
        // Owner data
        owner_email: ownerData.email,
        owner_name: ownerData.name,
        owner_password: ownerData.password
      });
      
      console.log('✅ Signup successful:', signupResponse.data);
      
      // Store the returned user and token
      const { user, token, message } = signupResponse.data;
      console.log('✅ Store and owner created:', { user, message });
      
      if (selectedPlan) {
        toast.success(message || 'Account created! Redirecting to payment...');
        console.log('✅ SIGNUP COMPLETE - redirecting to checkout for plan:', selectedPlan);
      } else {
        toast.success(message || 'Account created successfully! Please sign in.');
        console.log('✅ SIGNUP COMPLETE - redirecting to login');
      }
      onSuccess(user, token);
    } catch (error) {
      console.error('❌ SIGNUP FAILED - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Sign up failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      console.log('Loading state set to false');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="text-center relative">
            <button 
              onClick={onBack}
              className="absolute left-0 top-0 text-orange-600 hover:text-orange-700 flex items-center gap-1 text-sm sm:text-base"
              style={{minHeight: '44px', padding: '8px'}}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_bawarchi-catering-2/artifacts/6e6yv8t6_caterlog-high-resolution-logo-transparent.png" 
              alt="CaterLog" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Create Your CaterLog Account
            </h1>
            {selectedPlan && typeof selectedPlan === 'string' ? (
              <p className="text-gray-600">
                Selected: <span className="font-semibold text-orange-600">
                  {selectedPlan.includes('base') ? 'Base' : 'Premium'} Plan
                </span> - You'll be redirected to payment after signup
              </p>
            ) : (
              <p className="text-gray-600">Start your 14-day free trial</p>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className={`flex items-center ${step >= 1 ? 'text-orange-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-orange-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Store Info</span>
            </div>
            <div className="w-16 h-1 bg-gray-200"></div>
            <div className={`flex items-center ${step >= 2 ? 'text-orange-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-orange-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Owner Info</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="label-bawarchi">Store Name *</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={storeData.name}
                  onChange={(e) => setStoreData({...storeData, name: e.target.value})}
                  placeholder="Store Name that appears on invoices"
                />
              </div>
              <div>
                <label className="label-bawarchi">Phone Number *</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={storeData.phone}
                  onChange={(e) => setStoreData({...storeData, phone: e.target.value})}
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-bawarchi">City</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={storeData.city}
                    onChange={(e) => setStoreData({...storeData, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="label-bawarchi">State</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={storeData.state}
                    onChange={(e) => setStoreData({...storeData, state: e.target.value})}
                    placeholder="TX"
                  />
                </div>
              </div>
              <div>
                <label className="label-bawarchi">Address</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={storeData.address}
                  onChange={(e) => setStoreData({...storeData, address: e.target.value})}
                  placeholder="Street address"
                />
              </div>
              <button onClick={handleStoreNext} className="btn-bawarchi-primary w-full">
                Next: Owner Information
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="label-bawarchi">Your Full Name *</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={ownerData.name}
                  onChange={(e) => setOwnerData({...ownerData, name: e.target.value})}
                  placeholder="John Doe"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="label-bawarchi">Email Address *</label>
                <input
                  type="email"
                  className="input-bawarchi"
                  value={ownerData.email}
                  onChange={(e) => setOwnerData({...ownerData, email: e.target.value})}
                  placeholder="your.email@example.com"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="label-bawarchi">Password * (min 8 characters)</label>
                <div className="relative">
                  <input
                    type={showSignupPassword ? "text" : "password"}
                    className="input-bawarchi pr-12"
                    value={ownerData.password}
                    onChange={(e) => setOwnerData({...ownerData, password: e.target.value})}
                    placeholder="Create a strong password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="label-bawarchi">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="input-bawarchi pr-12"
                    value={ownerData.confirmPassword}
                    onChange={(e) => setOwnerData({...ownerData, confirmPassword: e.target.value})}
                    placeholder="Re-enter password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn-bawarchi-secondary flex-1" disabled={loading}>
                  Back
                </button>
                <button onClick={handleSignUp} className="btn-bawarchi-primary flex-1" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
              <p className="text-xs text-gray-600 text-center">
                By creating an account, you agree to our Terms of Service and Privacy Policy.
                {selectedPlan && typeof selectedPlan === 'string' && (selectedPlan.includes('base') || selectedPlan.includes('premium')) 
                  ? ' You\'ll be redirected to complete payment after signup.' 
                  : ' Your 14-day free trial starts immediately.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SignUpPage;